import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { api } from "../../api";
import {
  InvocationStatusEnum,
  type GenericPayload,
  type MyPayEnvironment,
} from "../../generated/core-api";
import {
  useCompanyProvisionAgentId,
  isTerminalInvocationStatus,
} from "../../onboarding";
import {
  isPayRunWritable,
  type MatchingImportWarning,
  type MatchingImportWorkflowResult,
  type MatchingRow,
  type ResolvedPacketRow,
} from "../types/matching";
import { mockResolveByPayRunId } from "../utils/fixtures/mockPayRuns";
import type { MatchingUxVariant } from "../types/uxVariant";

export interface ComponentMatchingImportManagerReturn {
  rows: MatchingRow[];
  companyCode: string | null;
  payRunId: string | null;
  mypayEnvironment: MyPayEnvironment | null;
  fileIdentifier: string | null;
  validationReport: MatchingImportWorkflowResult | null;
  validationStale: boolean;
  validationInputRevision: number;
  managerError: string | null;
  isValidating: boolean;
  isConfigLoading: boolean;
  hasRows: boolean;
  idempotencyKey: string | null;
  commitResult: MatchingImportWorkflowResult | null;
  isCommitting: boolean;
  hasUncommittedImportProgress: boolean;
  setPendingRows: (
    rows: MatchingRow[],
    companyCode: string,
    payRunId: string,
    mypayEnvironment: MyPayEnvironment,
    fileIdentifier?: string | null
  ) => void;
  updateRowsLocally: (rows: MatchingRow[]) => void;
  validate: () => Promise<void>;
  commit: (warningsAcknowledged: boolean) => Promise<void>;
  clear: () => void;
}

function normaliseResolvedRows(value: unknown): ResolvedPacketRow[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const rows: ResolvedPacketRow[] = [];
  for (const item of value) {
    if (typeof item !== "object" || item === null) continue;
    const r = item as Record<string, unknown>;
    const index = typeof r.index === "number" ? r.index : null;
    const employeeCode =
      typeof r.employeeCode === "string"
        ? r.employeeCode
        : typeof r.employee_code === "string"
          ? r.employee_code
          : null;
    const componentName =
      typeof r.componentName === "string"
        ? r.componentName
        : typeof r.component_name === "string"
          ? r.component_name
          : null;
    if (index === null || employeeCode === null || componentName === null) {
      continue;
    }
    const supportsRaw = r.supportsManualPay ?? r.supports_manual_pay;
    rows.push({
      index,
      employeeCode,
      componentName,
      payPacketId:
        typeof r.payPacketId === "string"
          ? r.payPacketId
          : typeof r.pay_packet_id === "string"
            ? r.pay_packet_id
            : null,
      componentCode:
        typeof r.componentCode === "string"
          ? r.componentCode
          : typeof r.component_code === "string"
            ? r.component_code
            : null,
      currentAmount:
        typeof r.currentAmount === "number"
          ? r.currentAmount
          : typeof r.current_amount === "number"
            ? r.current_amount
            : r.currentAmount != null
              ? Number(r.currentAmount)
              : r.current_amount != null
                ? Number(r.current_amount)
                : null,
      manualPayActive: Boolean(r.manualPayActive ?? r.manual_pay_active),
      retainedAmount: readOptionalAmount(r.retainedAmount ?? r.retained_amount),
      supportsManualPay:
        supportsRaw === undefined || supportsRaw === null
          ? true
          : Boolean(supportsRaw),
      companyComponentId:
        typeof r.companyComponentId === "string"
          ? r.companyComponentId
          : typeof r.company_component_id === "string"
            ? r.company_component_id
            : null,
      status:
        r.status === "not_found" ||
        r.status === "unsupported" ||
        r.status === "ambiguous"
          ? r.status
          : "ok",
    });
  }
  return rows;
}

export function isMatchingImportResult(
  value: unknown
): value is MatchingImportWorkflowResult {
  if (typeof value !== "object" || value === null) return false;
  const c = value as Record<string, unknown>;
  return (
    typeof c.total === "number" &&
    typeof c.succeeded === "number" &&
    typeof c.failed === "number" &&
    Array.isArray(c.errors)
  );
}

function coerceMatchingImportResult(
  value: MatchingImportWorkflowResult
): MatchingImportWorkflowResult {
  const raw = value as MatchingImportWorkflowResult & {
    resolved_rows?: unknown;
    warnings?: unknown;
  };
  return {
    ...value,
    resolvedRows:
      value.resolvedRows ?? normaliseResolvedRows(raw.resolved_rows),
  };
}

/**
 * Remap a commit result that was computed over an eligible subset of rows
 * (blocking-error / skip-warning rows withheld) back onto the full batch's
 * original indices. Reinstate withheld blocking errors and skip warnings so
 * those rows stay visible as error/warning — not "imported" — in the
 * post-commit report (#partial-import, mirrors Work Patterns).
 */
function remapPartialCommitResult(
  result: MatchingImportWorkflowResult,
  eligibleIndices: number[],
  skippedErrors: MatchingImportWorkflowResult["errors"],
  preservedSkipWarnings: MatchingImportWorkflowResult["warnings"],
  total: number
): MatchingImportWorkflowResult {
  const remappedErrors = result.errors.map((e) => ({
    ...e,
    index: e.index !== null ? (eligibleIndices[e.index] ?? e.index) : null,
  }));
  const remappedWarnings = (result.warnings ?? []).map((w) => ({
    ...w,
    indices: w.indices.map((i) => eligibleIndices[i] ?? i),
  }));
  const remappedResolvedRows = (result.resolvedRows ?? []).map((r) => ({
    ...r,
    index: eligibleIndices[r.index] ?? r.index,
  }));
  const skippedIndexCount = new Set(
    skippedErrors.map((e) => e.index).filter((i) => i !== null)
  ).size;
  return {
    total: total || result.total,
    succeeded: result.succeeded,
    failed: result.failed + skippedIndexCount,
    errors: [...remappedErrors, ...skippedErrors],
    // Skip-warning rows were never submitted — reinstate their original
    // (full-batch) warnings so clustering keeps status "warning", not "imported".
    warnings: [...remappedWarnings, ...(preservedSkipWarnings ?? [])],
    resolvedRows: remappedResolvedRows,
  };
}

function classifyInvocationError(error: unknown): string {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    if (status === 0 || !error.response) {
      return "Could not reach the API server. Make sure the API is running and try again.";
    }
    if (status === 404) {
      return "Agent not found (404). Check that VITE_ONBOARDING_AGENT_ID is set.";
    }
    if (status === 409) {
      return "This batch was already submitted. Start a new import if needed.";
    }
    if (status === 503) {
      return "Onboarding worker is not enabled (503). Set ONBOARDING_WORKER_ENABLED=true.";
    }
    return `API error (${status ?? "network"}): ${error.message}`;
  }
  return `Unexpected error: ${error instanceof Error ? error.message : String(error)}`;
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 150;

/** Local in-memory validate/commit fixtures — DEV/test only (fail closed elsewhere). */
function allowsLocalMatchingMock(): boolean {
  return import.meta.env.DEV || import.meta.env.MODE === "test";
}

const AGENT_NOT_CONFIGURED_MESSAGE =
  "Onboarding agent is not configured. Set ONBOARDING_AGENT_ID and " +
  "ONBOARDING_SERVICE_ACCOUNT_ID for this environment.";

const SKIP_WARNING_CODES = new Set(["MATCH-ALIGNED", "MATCH-NO-OVERRIDE"]);
const POLL_QUERY_KEY_ROOT = "componentMatchingImportInvocation";
/** Mock / local demo: keep the Work Patterns–style progress panel visible. */
const VALIDATE_MIN_VISIBLE_MS = 2500;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Local fixture derivation used when the agent id is unavailable. Packet ids
 * are built from each row's own `payRunId` (not a single batch-level value)
 * — Option B (infer-from-csv) has no bound pay run and rows may legitimately
 * span multiple pay runs; Option A stamps the same id onto every row anyway.
 */
function readOptionalAmount(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

interface LocalPacket {
  currentAmount: number | null;
  manualPayActive: boolean;
  retainedAmount: number | null;
  supportsManualPay: boolean;
  status: "ok" | "not_found" | "unsupported";
  companyComponentId: string | null;
}

/** Mirrors the API mock resolve catalogue for offline validate. */
function localPacket(row: MatchingRow): LocalPacket {
  if (row.componentName === "Override Active Comp") {
    return {
      currentAmount: 40,
      manualPayActive: true,
      retainedAmount: 40,
      supportsManualPay: true,
      status: "ok",
      companyComponentId: "cc-override-on",
    };
  }
  if (row.componentName === "Override Retained Comp") {
    return {
      currentAmount: 25,
      manualPayActive: false,
      retainedAmount: 40,
      supportsManualPay: true,
      status: "ok",
      companyComponentId: "cc-override-kept",
    };
  }
  if (row.componentName === "No Override Comp") {
    return {
      currentAmount: 10,
      manualPayActive: false,
      retainedAmount: null,
      supportsManualPay: true,
      status: "ok",
      companyComponentId: "cc-no-override",
    };
  }
  if (row.componentName === "Annual Leave") {
    return {
      currentAmount: 380,
      manualPayActive: false,
      retainedAmount: null,
      supportsManualPay: true,
      status: "ok",
      companyComponentId: "cc-al",
    };
  }
  if (row.componentName === "Sick Leave") {
    return {
      currentAmount: 420,
      manualPayActive: false,
      retainedAmount: null,
      supportsManualPay: true,
      status: "ok",
      companyComponentId: "cc-sl",
    };
  }
  return {
    currentAmount: row.targetAmount ?? 0,
    manualPayActive: false,
    retainedAmount: null,
    supportsManualPay: true,
    status: "ok",
    companyComponentId: "cc-demo",
  };
}

export function deriveLocalReport(rows: MatchingRow[]): MatchingImportWorkflowResult {
  const errors: MatchingImportWorkflowResult["errors"] = [];
  const warnings: NonNullable<MatchingImportWorkflowResult["warnings"]> = [];
  const resolvedRows: ResolvedPacketRow[] = [];

  const seen = new Map<string, number[]>();
  rows.forEach((row, index) => {
    const key = [
      row.employeeCode.trim().toUpperCase(),
      row.componentName.trim().toUpperCase(),
      (row.startDate ?? "").trim(),
      (row.finishDate ?? "").trim(),
    ].join("|");
    const list = seen.get(key) ?? [];
    list.push(index);
    seen.set(key, list);
  });
  for (const indices of seen.values()) {
    if (indices.length < 2) continue;
    for (const index of indices) {
      errors.push({
        index,
        employeeCode: rows[index].employeeCode,
        field: null,
        error:
          "Duplicate row in this file for the same employee, component, and assignment dates. Keep one row per line identity.",
      });
    }
  }

  const payRunIds = new Set(rows.map((r) => r.payRunId));
  for (const payRunId of payRunIds) {
    const resolved = mockResolveByPayRunId(payRunId);
    if (!resolved) {
      rows.forEach((row, index) => {
        if (row.payRunId !== payRunId) return;
        errors.push({
          index,
          employeeCode: row.employeeCode,
          field: "payRunId",
          error: `Pay run ID "${payRunId}" was not found for company lookup.`,
        });
      });
      continue;
    }
    if (isPayRunWritable(resolved.state)) continue;
    const message = `Matching is blocked while this pay run is ${resolved.state}. Choose a Pending or In Progress run.`;
    rows.forEach((row, index) => {
      if (row.payRunId !== payRunId) return;
      errors.push({
        index,
        employeeCode: row.employeeCode,
        field: "payRunId",
        error: message,
      });
    });
  }

  rows.forEach((row, index) => {
    if (row.employeeCode.toUpperCase() === "ENOEMP") {
      errors.push({
        index,
        employeeCode: row.employeeCode,
        field: "employeeCode",
        error: "Employee was not found in the bound pay run.",
      });
      resolvedRows.push({
        index,
        employeeCode: row.employeeCode,
        componentName: row.componentName,
        payPacketId: null,
        currentAmount: null,
        manualPayActive: false,
        retainedAmount: null,
        supportsManualPay: true,
        companyComponentId: null,
        status: "not_found",
      });
      return;
    }
    if (
      row.employeeCode.toUpperCase() === "E99001" &&
      row.componentName === "Annual Leave" &&
      !(row.startDate ?? "").trim() &&
      !(row.finishDate ?? "").trim()
    ) {
      errors.push({
        index,
        employeeCode: row.employeeCode,
        field: "componentName",
        error:
          "Could not accurately identify target component. More than one pay-packet line matches this employee and component — supply StartDate and FinishDate to pick one span, or fix duplicate assignments on Datapay desktop and re-upload.",
      });
      resolvedRows.push({
        index,
        employeeCode: row.employeeCode,
        componentName: row.componentName,
        payPacketId: null,
        currentAmount: null,
        manualPayActive: false,
        retainedAmount: null,
        supportsManualPay: true,
        companyComponentId: null,
        status: "ambiguous",
      });
      return;
    }
    if (row.componentName === "Tax code/Studen Loan") {
      errors.push({
        index,
        employeeCode: row.employeeCode,
        field: "componentName",
        error: "Component name could not be resolved.",
      });
      resolvedRows.push({
        index,
        employeeCode: row.employeeCode,
        componentName: row.componentName,
        payPacketId: null,
        currentAmount: null,
        manualPayActive: false,
        retainedAmount: null,
        supportsManualPay: false,
        companyComponentId: null,
        status: "not_found",
      });
      return;
    }
    if (row.componentName === "No Manual Pay Comp") {
      errors.push({
        index,
        employeeCode: row.employeeCode,
        field: "componentName",
        error: "This component does not support the manual pay override flag.",
      });
      resolvedRows.push({
        index,
        employeeCode: row.employeeCode,
        componentName: row.componentName,
        payPacketId: `PP-${row.payRunId}-${row.employeeCode}`,
        currentAmount: 50,
        manualPayActive: false,
        retainedAmount: null,
        supportsManualPay: false,
        companyComponentId: "cc-nomanual",
        status: "unsupported",
      });
      return;
    }

    const packet = localPacket(row);
    resolvedRows.push({
      index,
      employeeCode: row.employeeCode,
      componentName: row.componentName,
      payPacketId: `PP-${row.payRunId}-${row.employeeCode}`,
      currentAmount: packet.currentAmount,
      manualPayActive: packet.manualPayActive,
      retainedAmount: packet.retainedAmount,
      supportsManualPay: packet.supportsManualPay,
      companyComponentId: packet.companyComponentId,
      status: packet.status,
    });

    if (
      row.matchAction === "DELETE_KEEP_AMOUNT" &&
      !packet.manualPayActive
    ) {
      warnings.push({
        code: "MATCH-NO-OVERRIDE",
        employeeCode: row.employeeCode,
        indices: [index],
        warning:
          "No active override to turn off; this row will be skipped on commit.",
      });
    } else if (row.matchAction === "APPLY" && row.targetAmount === null) {
      // APPLY always needs an explicit TargetAmount (CSV + row-edit rules).
      // Do not treat a retained mock amount as satisfying APPLY — that is REAPPLY.
      errors.push({
        index,
        employeeCode: row.employeeCode,
        field: "targetAmount",
        error: "TargetAmount is required for APPLY.",
      });
    } else if (
      row.matchAction === "REAPPLY" &&
      packet.retainedAmount === null
    ) {
      errors.push({
        index,
        employeeCode: row.employeeCode,
        field: "targetAmount",
        error:
          "Nothing retained to reinstate. Use APPLY with a TargetAmount to set the override.",
      });
    } else if (
      row.matchAction === "APPLY" &&
      row.employeeCode.toUpperCase() === "E100" &&
      row.componentName === "Annual Leave" &&
      row.targetAmount === 380
    ) {
      warnings.push({
        code: "MATCH-ALIGNED",
        employeeCode: row.employeeCode,
        indices: [index],
        warning: "Amount already matches Datapay; this row will be skipped on commit.",
      });
    }

    // AC-22 / R-APP-16 — populated TargetUnits is ignored (amount-only matching).
    if ((row.extraColumns.TargetUnits ?? "").trim() !== "") {
      warnings.push({
        code: "MATCH-UNITS-IGNORED",
        employeeCode: row.employeeCode || null,
        indices: [index],
        warning:
          "TargetUnits is not applied for component matching; only TargetAmount is used.",
      });
    }
  });

  const failed = new Set(errors.map((e) => e.index).filter((i) => i !== null)).size;
  return {
    total: rows.length,
    succeeded: 0,
    failed,
    errors,
    warnings,
    resolvedRows,
  };
}

/** Wire row for `component_matching_import` — single MatchAction verb (#406). */
function toContractRow(row: MatchingRow) {
  const targetUnits = row.extraColumns.TargetUnits?.trim() || undefined;
  return {
    matchAction: row.matchAction,
    employeeCode: row.employeeCode,
    componentCode: row.componentCode,
    componentName: row.componentName,
    startDate: row.startDate,
    finishDate: row.finishDate,
    targetAmount: row.targetAmount,
    payRunId: row.payRunId,
    ...(targetUnits ? { targetUnits } : {}),
  };
}

/** AC-22 / R-APP-16 — merge client TargetUnits-ignored advisories into API validate. */
function withTargetUnitsWarnings(
  report: MatchingImportWorkflowResult,
  sourceRows: readonly MatchingRow[]
): MatchingImportWorkflowResult {
  const existing = report.warnings ?? [];
  const already = new Set(
    existing
      .filter((w) => w.code === "MATCH-UNITS-IGNORED")
      .flatMap((w) => w.indices)
  );
  const extra: MatchingImportWarning[] = [];
  sourceRows.forEach((row, index) => {
    if (already.has(index)) return;
    if ((row.extraColumns.TargetUnits ?? "").trim() === "") return;
    extra.push({
      code: "MATCH-UNITS-IGNORED",
      employeeCode: row.employeeCode || null,
      indices: [index],
      warning:
        "TargetUnits is not applied for component matching; only TargetAmount is used.",
    });
  });
  if (extra.length === 0) return report;
  return { ...report, warnings: [...existing, ...extra] };
}

export function useComponentMatchingImportInvocation(): ComponentMatchingImportManagerReturn {
  const {
    agentId,
    serviceAccountId,
    isLoading: isConfigLoading,
  } = useCompanyProvisionAgentId();
  const queryClient = useQueryClient();

  const [rows, setRows] = useState<MatchingRow[]>([]);
  const [companyCode, setCompanyCode] = useState<string | null>(null);
  const [payRunId, setPayRunId] = useState<string | null>(null);
  const [mypayEnvironment, setMypayEnvironment] =
    useState<MyPayEnvironment | null>(null);
  const [fileIdentifier, setFileIdentifier] = useState<string | null>(null);
  const [validationReport, setValidationReport] =
    useState<MatchingImportWorkflowResult | null>(null);
  const [commitResult, setCommitResult] =
    useState<MatchingImportWorkflowResult | null>(null);
  const [validationStale, setValidationStale] = useState(false);
  const [validationInputRevision, setValidationInputRevision] = useState(0);
  const [managerError, setManagerError] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [activeMode, setActiveMode] = useState<"validate" | "commit" | null>(
    null
  );
  const modeRef = useRef<"validate" | "commit" | null>(null);
  const validateStartedAtRef = useRef<number | null>(null);
  /** Mirrors `validationInputRevision` for stale-result guards after await/poll. */
  const validationInputRevisionRef = useRef(0);
  /**
   * Revision captured when the current validate/commit was started. If the
   * user edits rows while a poll is in flight, the revision bumps and the
   * late result must be discarded (mirrors Work Patterns).
   */
  const activeRequestRevisionRef = useRef<number | null>(null);
  /** Partial commit: original indices of rows actually sent (no blocking/skip). */
  const commitEligibleIndicesRef = useRef<number[]>([]);
  /** Partial commit: pre-existing blocking errors for rows that were skipped (not sent). */
  const commitSkippedErrorsRef = useRef<MatchingImportWorkflowResult["errors"]>(
    []
  );
  /**
   * Partial commit: MATCH-ALIGNED / MATCH-NO-OVERRIDE warnings for rows
   * withheld from the write path — reinjected into the commit result so they
   * do not render as "imported".
   */
  const commitSkippedWarningsRef = useRef<
    MatchingImportWorkflowResult["warnings"]
  >([]);
  /** Partial commit: total row count in the batch at commit time. */
  const commitTotalRef = useRef(0);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const poll = useQuery({
    queryKey: [POLL_QUERY_KEY_ROOT, agentId, workflowId],
    queryFn: () =>
      api.getInvocationStatus(agentId as string, workflowId as string),
    enabled: !!agentId && !!workflowId,
    // Keep polling through transport errors — dataUpdateCount alone would
    // never advance and leave the UI stuck in validating/committing.
    retry: false,
    refetchInterval: (query) => {
      if (isTerminalInvocationStatus(query.state.data?.status)) return false;
      const attempts = Math.max(
        query.state.dataUpdateCount,
        query.state.errorUpdateCount
      );
      if (attempts >= MAX_POLL_ATTEMPTS) return false;
      return POLL_INTERVAL_MS;
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const pollQueryState = queryClient.getQueryState([
    POLL_QUERY_KEY_ROOT,
    agentId,
    workflowId,
  ]);
  const pollAttempts = Math.max(
    pollQueryState?.dataUpdateCount ?? 0,
    pollQueryState?.errorUpdateCount ?? 0
  );
  const pollTimedOut =
    !!workflowId &&
    !isTerminalInvocationStatus(poll.data?.status) &&
    pollAttempts >= MAX_POLL_ATTEMPTS;

  useEffect(() => {
    if (!pollTimedOut || !activeMode) return;
    setManagerError(
      poll.isError
        ? "Lost connection while waiting for the matching import workflow. Try again."
        : "Timed out waiting for the matching import workflow. Try again."
    );
    setWorkflowId(null);
    setActiveMode(null);
    modeRef.current = null;
    validateStartedAtRef.current = null;
    activeRequestRevisionRef.current = null;
  }, [activeMode, poll.isError, pollTimedOut]);

  useEffect(() => {
    if (
      !poll.data ||
      !isTerminalInvocationStatus(poll.data.status) ||
      !workflowId ||
      !activeMode
    ) {
      return;
    }
    const result = poll.data.result;
    if (
      poll.data.status === InvocationStatusEnum.COMPLETED &&
      isMatchingImportResult(result)
    ) {
      const normalised = coerceMatchingImportResult(result);
      const mode = activeMode;
      const startedAt = validateStartedAtRef.current;
      // Clear workflowId immediately so this effect does not re-enter while we
      // hold the progress panel for the remaining mock delay.
      setWorkflowId(null);

      void (async () => {
        // Mock UX delay only — real Temporal already took wall-clock time.
        if (
          mode === "validate" &&
          startedAt != null &&
          (!agentId || !serviceAccountId)
        ) {
          const remaining =
            VALIDATE_MIN_VISIBLE_MS - (Date.now() - startedAt);
          if (remaining > 0) await sleep(remaining);
        }
        if (modeRef.current !== mode) return;
        // Discard late results if rows/edits changed after this request started.
        if (
          activeRequestRevisionRef.current !== null &&
          activeRequestRevisionRef.current !==
            validationInputRevisionRef.current
        ) {
          setActiveMode(null);
          modeRef.current = null;
          validateStartedAtRef.current = null;
          activeRequestRevisionRef.current = null;
          return;
        }
        if (mode === "validate") {
          setValidationReport(
            withTargetUnitsWarnings(normalised, rowsRef.current)
          );
          setValidationStale(false);
          setManagerError(null);
        } else if (mode === "commit") {
          setCommitResult(
            remapPartialCommitResult(
              normalised,
              commitEligibleIndicesRef.current,
              commitSkippedErrorsRef.current,
              commitSkippedWarningsRef.current,
              commitTotalRef.current
            )
          );
          setManagerError(null);
        }
        setActiveMode(null);
        modeRef.current = null;
        validateStartedAtRef.current = null;
        activeRequestRevisionRef.current = null;
      })();
      return;
    }
    if (poll.data.status === InvocationStatusEnum.COMPLETED) {
      setManagerError(
        "Matching import finished but returned an unexpected result payload."
      );
      setWorkflowId(null);
      setActiveMode(null);
      modeRef.current = null;
      validateStartedAtRef.current = null;
      activeRequestRevisionRef.current = null;
      return;
    }
    if (
      poll.data.status === InvocationStatusEnum.FAILED ||
      poll.data.status === InvocationStatusEnum.TERMINATED
    ) {
      setManagerError(
        typeof poll.data.error === "string"
          ? poll.data.error
          : "Workflow failed."
      );
      setWorkflowId(null);
      setActiveMode(null);
      modeRef.current = null;
      validateStartedAtRef.current = null;
      activeRequestRevisionRef.current = null;
    }
  }, [activeMode, agentId, poll.data, serviceAccountId, workflowId]);

  const invokeMutation = useMutation({
    mutationFn: (args: {
      agentId: string;
      serviceAccountId: string;
      mode: "validate" | "commit";
      companyCode: string;
      payRunId: string;
      mypayEnvironment: MyPayEnvironment;
      fileIdentifier: string;
      idempotencyKey: string;
      rows: MatchingRow[];
    }) =>
      api.invokeAgent(args.agentId, {
        service_account_id: args.serviceAccountId,
        input_payload: {
          workflow: "component_matching_import",
          mode: args.mode,
          company_code: args.companyCode,
          pay_run_id: args.payRunId,
          // Bind-pay-run-first only — always enforce row ↔ bound pay run match.
          ux_variant: "target-first" satisfies MatchingUxVariant,
          mypay_environment: args.mypayEnvironment,
          file_identifier: args.fileIdentifier,
          idempotency_key: args.idempotencyKey,
          rows: args.rows.map(toContractRow),
        } as GenericPayload,
      }),
  });

  const setPendingRows = useCallback(
    (
      nextRows: MatchingRow[],
      nextCompany: string,
      nextPayRunId: string,
      nextEnv: MyPayEnvironment,
      nextFile?: string | null
    ) => {
      const rowsChanged =
        nextRows.length !== rows.length ||
        nextRows.some((r, i) => {
          const prev = rows[i];
          return (
            !prev ||
            r.employeeCode !== prev.employeeCode ||
            r.componentName !== prev.componentName ||
            r.componentCode !== prev.componentCode ||
            r.targetAmount !== prev.targetAmount ||
            r.matchAction !== prev.matchAction ||
            r.manualPay !== prev.manualPay ||
            r.amountMode !== prev.amountMode ||
            r.payRunId !== prev.payRunId ||
            r.startDate !== prev.startDate ||
            r.finishDate !== prev.finishDate
          );
        });
      const bindingChanged =
        nextCompany !== companyCode ||
        nextPayRunId !== payRunId ||
        nextEnv !== mypayEnvironment ||
        (nextFile ?? null) !== fileIdentifier;
      const invalidate =
        validationReport !== null && (rowsChanged || bindingChanged);
      setRows(nextRows);
      setCompanyCode(nextCompany);
      setPayRunId(nextPayRunId);
      setMypayEnvironment(nextEnv);
      setFileIdentifier(nextFile ?? null);
      setCommitResult(null);
      setIdempotencyKey(null);
      setValidationInputRevision((r) => {
        const next = r + 1;
        validationInputRevisionRef.current = next;
        return next;
      });
      if (invalidate) {
        setValidationStale(true);
        setValidationReport(null);
      } else if (validationReport === null) {
        setValidationStale(false);
      }
    },
    [
      companyCode,
      fileIdentifier,
      mypayEnvironment,
      payRunId,
      rows,
      validationReport,
    ]
  );

  const updateRowsLocally = useCallback((nextRows: MatchingRow[]) => {
    setRows(nextRows);
    setValidationStale(true);
    setCommitResult(null);
    setIdempotencyKey(null);
    setValidationInputRevision((r) => {
      const next = r + 1;
      validationInputRevisionRef.current = next;
      return next;
    });
  }, []);

  const validate = useCallback(async () => {
    if (!companyCode || !payRunId || !mypayEnvironment || rows.length === 0) {
      setManagerError("Bind company, pay run, and upload rows before validating.");
      return;
    }
    if (!fileIdentifier) {
      setManagerError("No file identifier found. Please upload a file before validating.");
      return;
    }
    setManagerError(null);
    setCommitResult(null);
    // Keep the previous report mounted as stale while validating so the table
    // (search / clusters / selection) does not unmount between bulk apply and
    // the new result. Commit stays gated on !validationStale.
    modeRef.current = "validate";
    setActiveMode("validate");
    validateStartedAtRef.current = Date.now();
    const revisionAtStart = validationInputRevisionRef.current;
    activeRequestRevisionRef.current = revisionAtStart;

    // Local mock only in DEV/test — production/preview must fail closed.
    if (!agentId || !serviceAccountId) {
      if (!allowsLocalMatchingMock()) {
        setManagerError(AGENT_NOT_CONFIGURED_MESSAGE);
        setActiveMode(null);
        modeRef.current = null;
        validateStartedAtRef.current = null;
        activeRequestRevisionRef.current = null;
        return;
      }
      await sleep(VALIDATE_MIN_VISIBLE_MS);
      if (validationInputRevisionRef.current !== revisionAtStart) {
        setActiveMode(null);
        modeRef.current = null;
        validateStartedAtRef.current = null;
        activeRequestRevisionRef.current = null;
        return;
      }
      const report = deriveLocalReport(rows);
      setValidationReport(report);
      setValidationStale(false);
      setIdempotencyKey(`local-${crypto.randomUUID()}`);
      setActiveMode(null);
      modeRef.current = null;
      validateStartedAtRef.current = null;
      activeRequestRevisionRef.current = null;
      return;
    }

    const key = crypto.randomUUID();
    setIdempotencyKey(key);
    try {
      const invocation = await invokeMutation.mutateAsync({
        agentId,
        serviceAccountId,
        mode: "validate",
        companyCode,
        payRunId,
        mypayEnvironment,
        fileIdentifier,
        idempotencyKey: key,
        rows,
      });
      setWorkflowId(invocation.workflow_id);
      void queryClient.invalidateQueries({
        queryKey: [POLL_QUERY_KEY_ROOT, agentId, invocation.workflow_id],
      });
    } catch (error) {
      setManagerError(classifyInvocationError(error));
      setActiveMode(null);
      modeRef.current = null;
      validateStartedAtRef.current = null;
    }
  }, [
    agentId,
    companyCode,
    fileIdentifier,
    invokeMutation,
    mypayEnvironment,
    payRunId,
    queryClient,
    rows,
    serviceAccountId,
  ]);

  const commit = useCallback(
    async (warningsAcknowledged: boolean) => {
      if (
        !companyCode ||
        !payRunId ||
        !mypayEnvironment ||
        !fileIdentifier ||
        !idempotencyKey ||
        !validationReport ||
        validationStale ||
        !warningsAcknowledged
      ) {
        return;
      }

      // Batch-level findings (index null) block the whole commit — partial
      // import only skips per-row errors.
      const batchErrors = validationReport.errors.filter(
        (e) => e.index === null
      );
      if (batchErrors.length > 0) {
        setManagerError(
          batchErrors[0]?.error ??
            "Validation has batch-level errors. Fix the file and re-validate."
        );
        return;
      }

      // Partial import (#confirm-with-errors, mirrors Work Patterns): rows with
      // a blocking validation error are withheld from the commit call and
      // reinstated as errors in the result so they stay visible, not silently
      // dropped. MATCH-ALIGNED / MATCH-NO-OVERRIDE promise a skip — exclude
      // them from the write path so they are not counted as succeeded.
      const blockingIndices = new Set(
        validationReport.errors
          .filter((e) => e.index !== null)
          .map((e) => e.index as number)
      );
      const skippedWarnings = (validationReport.warnings ?? []).filter((w) =>
        SKIP_WARNING_CODES.has(w.code)
      );
      const skipWarningIndices = new Set(
        skippedWarnings.flatMap((w) => w.indices)
      );
      const eligibleIndices = rows
        .map((_, i) => i)
        .filter((i) => !blockingIndices.has(i) && !skipWarningIndices.has(i));
      const skippedErrors = validationReport.errors.filter(
        (e) => e.index !== null && blockingIndices.has(e.index as number)
      );

      // AC-23: warning-only skip batch — confirm with zero writes (no API call).
      if (eligibleIndices.length === 0 && blockingIndices.size === 0) {
        setManagerError(null);
        setCommitResult({
          total: rows.length,
          succeeded: 0,
          failed: 0,
          errors: [],
          warnings: validationReport.warnings,
          resolvedRows: validationReport.resolvedRows,
        });
        return;
      }

      if (eligibleIndices.length === 0) {
        setManagerError(
          "No rows are eligible to import. Fix the errors and re-validate."
        );
        return;
      }
      const eligibleRows = eligibleIndices.map((i) => rows[i]);

      setManagerError(null);
      modeRef.current = "commit";
      setActiveMode("commit");
      const revisionAtStart = validationInputRevisionRef.current;
      activeRequestRevisionRef.current = revisionAtStart;
      commitEligibleIndicesRef.current = eligibleIndices;
      commitSkippedErrorsRef.current = skippedErrors;
      commitSkippedWarningsRef.current = skippedWarnings;
      commitTotalRef.current = rows.length;

      if (!agentId || !serviceAccountId) {
        if (!allowsLocalMatchingMock()) {
          setManagerError(AGENT_NOT_CONFIGURED_MESSAGE);
          setActiveMode(null);
          modeRef.current = null;
          activeRequestRevisionRef.current = null;
          return;
        }
        // Local mock: validationReport's warnings/resolvedRows already use the
        // full batch's original indices (no subset remap needed here, unlike
        // the real backend path below which responds relative to eligibleRows).
        await sleep(VALIDATE_MIN_VISIBLE_MS);
        if (validationInputRevisionRef.current !== revisionAtStart) {
          setActiveMode(null);
          modeRef.current = null;
          activeRequestRevisionRef.current = null;
          return;
        }
        setCommitResult({
          total: rows.length,
          succeeded: eligibleRows.length,
          failed: blockingIndices.size,
          errors: skippedErrors,
          // Skip-warning rows were not written — keep warnings so they stay visible.
          warnings: validationReport.warnings,
          resolvedRows: validationReport.resolvedRows,
        });
        setActiveMode(null);
        modeRef.current = null;
        activeRequestRevisionRef.current = null;
        return;
      }

      try {
        const invocation = await invokeMutation.mutateAsync({
          agentId,
          serviceAccountId,
          mode: "commit",
          companyCode,
          payRunId,
          mypayEnvironment,
          fileIdentifier,
          idempotencyKey,
          rows: eligibleRows,
        });
        setWorkflowId(invocation.workflow_id);
      } catch (error) {
        setManagerError(classifyInvocationError(error));
        setActiveMode(null);
        modeRef.current = null;
        activeRequestRevisionRef.current = null;
      }
    },
    [
      agentId,
      companyCode,
      fileIdentifier,
      idempotencyKey,
      invokeMutation,
      mypayEnvironment,
      payRunId,
      rows,
      serviceAccountId,
      validationReport,
      validationStale,
    ]
  );

  const clear = useCallback(() => {
    setRows([]);
    setCompanyCode(null);
    setPayRunId(null);
    setMypayEnvironment(null);
    setFileIdentifier(null);
    setValidationReport(null);
    setCommitResult(null);
    setValidationStale(false);
    setManagerError(null);
    setIdempotencyKey(null);
    setWorkflowId(null);
    setActiveMode(null);
    setValidationInputRevision(0);
    validationInputRevisionRef.current = 0;
    activeRequestRevisionRef.current = null;
    modeRef.current = null;
    validateStartedAtRef.current = null;
  }, []);

  // Keep validate/commit busy flags mode-scoped so commit never paints
  // "Validating…" (Work Patterns uses separate mutations for the same reason).
  const isValidating =
    activeMode === "validate" ||
    (invokeMutation.isPending && modeRef.current === "validate");
  const isCommitting =
    activeMode === "commit" ||
    (invokeMutation.isPending && modeRef.current === "commit");

  return useMemo(
    () => ({
      rows,
      companyCode,
      payRunId,
      mypayEnvironment,
      fileIdentifier,
      validationReport,
      validationStale,
      validationInputRevision,
      managerError,
      isValidating,
      isConfigLoading,
      hasRows: rows.length > 0,
      idempotencyKey,
      commitResult,
      isCommitting,
      hasUncommittedImportProgress:
        rows.length > 0 && commitResult === null,
      setPendingRows,
      updateRowsLocally,
      validate,
      commit,
      clear,
    }),
    [
      rows,
      companyCode,
      payRunId,
      mypayEnvironment,
      fileIdentifier,
      validationReport,
      validationStale,
      validationInputRevision,
      managerError,
      isValidating,
      isConfigLoading,
      idempotencyKey,
      commitResult,
      isCommitting,
      setPendingRows,
      updateRowsLocally,
      validate,
      commit,
      clear,
    ]
  );
}
